/* eslint-disable import-x/no-mutable-exports */
/* oxlint-disable vars-on-top */
/* oxlint-disable no-var */
import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import {FromServerProtocol, Protocol} from 'notebook/ELNConstants';
import formatters from 'notebook/mediocre/formatted-text/formatters';
import {Format} from 'notebook/mediocre/formatted-text/FormattersConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import SelectorDAO from 'notebook/plain-tables/selectors/SelectorDAO';
import {LEVEL_MATERIAL, LEVEL_COMPONENT, Material, Component} from 'notebook/protocols/MaterialConstants';
import {BaseStep, StepLevel} from 'notebook/protocols/StepConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {
  createNoteRuntimeSnapshot,
  FromServerNoteRuntimeSnapshot,
  NoteRuntimeSnapshot,
  prepareNoteRuntimeSnapshotForSave,
  prepareNoteRuntimeSnapshotForStore,
} from 'notebook/tables/TableStoreTransforms';
import TableUtil from 'notebook/tables/TableUtil';
import {omit} from 'util/omitKey';
import pick from 'util/pick';
import uniqueId from 'util/uniqueId';

type ProtocolStoreTransformsMaterial = Omit<Material, 'creator_id' | 'link_type'>;
type ProtocolStoreTransformsStep = Omit<
  BaseStep<{
    id?: number;
    cid: string;
  }>,
  'creator_id' | 'image' | 'timer'
> & {
  stepCount?: number;
  link_type: string;
};
export type ProtocolStoreTransformsProtocol = Omit<
  FromServerProtocol,
  'steps' | 'materials' | 'versionId' | 'noteRuntimeSnapshot'
> & {
  cid?: string;
  steps: Array<ProtocolStoreTransformsStep>;
  materials: Array<ProtocolStoreTransformsMaterial>;
  versionId?: number;
  noteRuntimeSnapshot: FromServerNoteRuntimeSnapshot | NoteRuntimeSnapshot;
};

function updateCalculatedIndices(protocol: ProtocolStoreTransformsProtocol): ProtocolStoreTransformsProtocol {
  let currentCount = 0;
  const stepUpdates: {[index: number]: Spec<ProtocolStoreTransformsStep, never>} = {};
  protocol.steps.forEach((step, i) => {
    let stepCount;
    if (step.level === StepLevel.STEP) {
      currentCount += 1;
      stepCount = currentCount;
      if (stepCount === step.stepCount) {
        return;
      }
      stepUpdates[i] = {$merge: {stepCount}};
    } else {
      if (step.stepCount == null) {
        return;
      }
      stepUpdates[i] = {$set: omit(step, 'stepCount')};
    }
  });
  if (!Object.keys(stepUpdates).length) {
    return protocol;
  }
  return reactUpdate(protocol, {steps: stepUpdates});
}

function makeMaterial(
  text: string,
  formats: Format,
  components: Array<Component> = []
): ProtocolStoreTransformsMaterial {
  assert(text != null && formats != null, 'Expected non-null text, formats, and components');
  return {
    name: text,
    cid: uniqueId('m'),
    components,
    formats,
  };
}

function makeImage(): {id: null; cid: string; level: string; image: {key: string}} {
  return {
    id: null,
    cid: uniqueId('i'),
    level: StepLevel.IMAGE,

    image: {
      key: '',
    },
  };
}

function makeTimer(): {
  id: null;
  cid: string;
  timer: {milliseconds: number};
  time_seconds: null;
  level: string;
} {
  return {
    id: null,
    cid: uniqueId('t'),
    timer: {milliseconds: 60 * 1000},
    time_seconds: null,
    level: StepLevel.TIMER,
  };
}

function makeTable(
  tableName: string,
  table: Table | null = null,
  rows: number,
  cols: number,
  param: {isWellPlate: boolean} = {isWellPlate: false}
): {
  time_seconds: null;
  table: Table;
  level: StepLevel;
  id: null;
  link_type: string;
  cid: string;
} {
  const {isWellPlate} = param;
  if (table == null) {
    table = TableUtil.defaultContent(tableName, rows, cols, {isWellPlate});
  }
  assert(table != null, 'Expected non-null table');
  table.name = tableName;
  return {
    time_seconds: null,
    table,
    level: StepLevel.TABLE,
    id: null,
    link_type: 'step',
    cid: uniqueId('s'),
  };
}

function makeStep(
  text: string,
  formats: Format,
  level: StepLevel,
  type: string | null = null
): ProtocolStoreTransformsStep {
  assert(text != null && formats != null && level != null, 'Expected non-null text, formats, and level');
  return {
    time_seconds: null,
    text,
    formats,
    level,
    link_type: 'step',
    cid: uniqueId('s'),
    completed: false,
    type,
    table: null,
  };
}

function makeComponent(text: string, formats: Format): Component {
  assert(text != null && formats != null, 'Expected non-null text and formats');
  return {
    raw_text: text,
    cid: uniqueId('c'),
    formats,
  };
}

var ProtocolStoreTransforms = {
  makeMaterial,
  makeImage,
  makeTimer,
  makeTable,
  makeStep,
  makeComponent,

  getMaterialByIndex(
    protocol: Protocol,
    index: [number, number?]
  ):
    | {
        model: Material;
        material: Material;
        component: null;
        level: typeof LEVEL_MATERIAL;
      }
    | {
        model: Component;
        component: Component;
        material: Material;
        level: typeof LEVEL_COMPONENT;
      }
    | undefined {
    const material = protocol.materials[index[0]];
    // oxlint-disable-next-line default-case
    switch (index.length) {
      case 1:
        return {
          model: material,
          material,
          component: null,
          level: LEVEL_MATERIAL,
        };
      case 2: {
        const component = material.components[index[1]!];
        return {
          model: component,
          component,
          material,
          level: LEVEL_COMPONENT,
        };
      }
    }
  },

  pickRevertableFields(
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
  ): {materials: unknown; steps: unknown; description: unknown; descriptionFormats: unknown; name: string} {
    return pick(data, 'materials', 'steps', 'description', 'descriptionFormats', 'name');
  },

  sanitizeProtocol(
    protocol: ProtocolStoreTransformsProtocol,
    oldProtocol: ProtocolStoreTransformsProtocol | null = null
  ): ProtocolStoreTransformsProtocol {
    if (protocol.steps.length === 0) {
      protocol = reactUpdate(protocol, {
        $merge: {
          steps: [makeStep('', formatters.defaultFormats(), StepLevel.HEADER)],
        },
      });
    }
    protocol = updateCalculatedIndices(protocol);
    return protocol;
  },

  prepareForStore(
    protocol: ProtocolStoreTransformsProtocol,
    cidIdMap: CidIdMap
  ): ProtocolStoreTransformsProtocol {
    // Register ids in cid map, and remove id
    function addCid(
      item: ProtocolStoreTransformsMaterial | Component | ProtocolStoreTransformsStep,
      prefix: string
    ): void {
      assert(item.cid == null, 'Expected non-null item.cid');
      item.cid = uniqueId(prefix);
      cidIdMap[item.cid] = item.id!;
      // TODO(eln): Delete ids once protocols are versioned.
      // Completing step logic and Commentable has a linkableID prop that would need fixing.
      // delete item.id
    }

    protocol.cid = uniqueId('pv');
    cidIdMap[protocol.cid] = protocol.versionId!;
    delete protocol.versionId;

    const {selectors, materials, steps} = protocol;
    protocol.selectors = selectors.map((selector) => SelectorDAO.prepareForStore(selector, cidIdMap));
    if (protocol.noteRuntimeSnapshot) {
      prepareNoteRuntimeSnapshotForStore(
        protocol.noteRuntimeSnapshot as FromServerNoteRuntimeSnapshot,
        cidIdMap
      );
    } else {
      protocol.noteRuntimeSnapshot = createNoteRuntimeSnapshot();
    }
    for (const material of Array.from(materials)) {
      addCid(material, 'm');
      for (const component of Array.from(material.components)) {
        addCid(component, 'c');
      }
    }
    for (const step of Array.from(steps)) {
      addCid(step, 's');
    }

    // We only do this sanitization on the first load, so we do it here
    // instead of in sanitizeItem
    protocol = reactUpdate(protocol, {
      materials: {
        $set:
          protocol.materials.length === 0
            ? [
                makeMaterial('', formatters.defaultFormats(), [
                  makeComponent('', formatters.defaultFormats()),
                ]),
              ]
            : protocol.materials,
      },
      steps: {
        $set:
          protocol.steps.length === 0
            ? [
                makeStep('', formatters.defaultFormats(), StepLevel.HEADER),
                makeStep('', formatters.defaultFormats(), StepLevel.STEP),
              ]
            : protocol.steps.map((step) => {
                if (step.table != null) {
                  step.table = TableUtil.prepareForStore(step.table);
                }
                return step;
              }),
      },
    });
    return protocol;
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  prepareForSave(protocol: ProtocolStoreTransformsProtocol, cidIdMap: CidIdMap) {
    // TODO(T1044): preparedProtocol should be keyed under protocolVersion
    const preparedProtocol = ProtocolStoreTransforms.preparePVForSave(protocol, cidIdMap);
    return {
      ...preparedProtocol,
      name: protocol.name,
    };
  },

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  preparePVForSave(protocolVersion: ProtocolStoreTransformsProtocol, cidIdMap: CidIdMap) {
    return {
      id: cidIdMap[protocolVersion.cid!],
      cid: protocolVersion.cid,
      description: protocolVersion.description,
      descriptionFormats: protocolVersion.descriptionFormats,
      noteRuntimeSnapshot: prepareNoteRuntimeSnapshotForSave(
        protocolVersion.noteRuntimeSnapshot as NoteRuntimeSnapshot,
        cidIdMap
      ),
      selectors: protocolVersion.selectors.map((selector) => SelectorDAO.prepareForSave(selector, cidIdMap)),
      steps: Array.from(protocolVersion.steps).map((step) =>
        _.chain(step)
          .pick(
            'cid',
            'level',
            'text',
            'time_seconds',
            'completed',
            'type',
            'table',
            'image',
            'timer',
            'formats'
          )
          .extend({id: cidIdMap[step.cid]})
          .value()
      ),
      materials: Array.from(protocolVersion.materials).map((material) =>
        _.chain(material)
          .pick('cid', 'name', 'components', 'formats')
          .extend({
            id: cidIdMap[material.cid],
            components: Array.from(material.components).map((component) =>
              _.chain(component)
                .pick('cid', 'raw_text', 'formats')
                .extend({id: cidIdMap[component.cid]})
                .value()
            ),
          })
          .value()
      ),
    };
  },
};

export default ProtocolStoreTransforms;
